import React from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  Button,
  DropdownMenuItem,
  MultiDropdown,
  Paper,
  Stack,
  Typography,
} from 'views/components';
import {Translate, translate} from 'views/module';

import {PROFILE_CATEGORY_KEYS, PROFILE_CATEGORY_SIGNAL_KEYS} from './utils';

const ProfileCategoryFilters = (props) => {
  const {
    disabled,
    matchedCount,
    missingCategories,
    onClear,
    onMissingCategoriesChange,
    totalCount,
  } = props;
  const hasCategoryFilters = missingCategories.length > 0;

  return (
    <Paper variant='outlined' sx={{p: 2}}>
      <Stack spacing={2}>
        <Stack
          direction={{xs: 'column', sm: 'row'}}
          spacing={1}
          alignItems={{xs: 'flex-start', sm: 'center'}}
          justifyContent='space-between'
        >
          <Stack spacing={0.5}>
            <Typography sx={{fontWeight: 700}}>
              <Translate expression='bulkMessaging.ProfileCategories.Title' />
            </Typography>
            <Typography color='text.secondary' sx={{fontSize: '13px'}}>
              <Translate expression='bulkMessaging.ProfileCategories.Description' />
            </Typography>
          </Stack>
          <Button
            disabled={disabled || !hasCategoryFilters}
            onClick={onClear}
            variant='outlined'
          >
            <Translate expression='bulkMessaging.ProfileCategories.Clear' />
          </Button>
        </Stack>

        <MultiDropdown
          disabled={disabled}
          label={translate('bulkMessaging.ProfileCategories.MissingCategories')}
          onChange={onMissingCategoriesChange}
          value={missingCategories}
        >
          {
            PROFILE_CATEGORY_KEYS.map((category) => (
              <DropdownMenuItem key={category} value={category}>
                {translate(`bulkMessaging.ProfileCategories.Options.${category}`)}
              </DropdownMenuItem>
            ))
          }
        </MultiDropdown>

        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: {xs: '1fr', md: 'repeat(2, minmax(0, 1fr))'},
          }}
        >
          {
            PROFILE_CATEGORY_KEYS.map((category) => (
              <Paper
                key={category}
                variant='outlined'
                sx={{
                  borderColor: missingCategories.includes(category) ? 'primary.main' : 'divider',
                  p: 1.5,
                }}
              >
                <Typography sx={{fontWeight: 700}}>
                  {translate(`bulkMessaging.ProfileCategories.Options.${category}`)}
                </Typography>
                <Stack component='ul' spacing={0.25} sx={{m: 0, mt: 0.5, pl: 2}}>
                  {
                    PROFILE_CATEGORY_SIGNAL_KEYS[category].map((signal) => (
                      <Typography
                        key={signal}
                        component='li'
                        color='text.secondary'
                        sx={{fontSize: '13px'}}
                      >
                        {translate(`bulkMessaging.ProfileCategories.Signals.${signal}`)}
                      </Typography>
                    ))
                  }
                </Stack>
              </Paper>
            ))
          }
        </Box>

        <Typography color={hasCategoryFilters ? 'text.primary' : 'warning.main'} sx={{fontSize: '13px'}}>
          <Translate
            expression={hasCategoryFilters ?
              'bulkMessaging.ProfileCategories.Matched' :
              'bulkMessaging.ProfileCategories.Required'}
            substitute={{matched: matchedCount, total: totalCount}}
          />
        </Typography>
      </Stack>
    </Paper>
  );
};

ProfileCategoryFilters.propTypes = {
  disabled: PropTypes.bool.isRequired,
  matchedCount: PropTypes.number.isRequired,
  missingCategories: PropTypes.arrayOf(PropTypes.oneOf(PROFILE_CATEGORY_KEYS)).isRequired,
  onClear: PropTypes.func.isRequired,
  onMissingCategoriesChange: PropTypes.func.isRequired,
  totalCount: PropTypes.number.isRequired,
};

export default React.memo(ProfileCategoryFilters);
